import { useEffect, useState } from "react";
import { Drawer } from "../../Drawer";
import { ShipmentCustomerDetails } from "../../CreateShipment/ShipmentCustomerDetails";
import {
  Stack,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  Typography,
  Divider,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { CardWithCollapse } from "../../CardWithCollapse/CardWithCollapse";
import { useGetSKU } from "../../../hooks/useGetSKU";
import { globalTheme } from "../../../styles";
import { useCreateManualOrder } from "../../../hooks/useCreateManualOrder";
import { useSearchOrderShipments } from "../../../hooks";
import { PAGINATION_PAGE_COUNT_OPTIONS } from "../../../constants";
import { ISearchOrderShipmentsOptions } from "../../../interfaces";

interface ManualCreateShipmentItemDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const ManualCreateOrderItemDrawer = ({ open, onClose }: ManualCreateShipmentItemDrawerProps) => {
  const [skuFieldValue, setSkuFieldValue] = useState<{ sku: string; qty: number }[]>([{ sku: "", qty: 1 || 0 }]);

  const [getSKUEnabled, setGetSKUEnabled] = useState<boolean>(false);
  const [disabledSubmit, setDisableSubmit] = useState<boolean>(true);
  const { loadingGetSKU, fetchingGetSKU, errorFetchingGetSKU, getSKUResponse, fetchGetSKUError, refetchGetSKU } = useGetSKU({
    skuData: { productSkus: skuFieldValue?.flatMap((sku) => sku.sku) },
    enabled: getSKUEnabled,
  });

  const generateTimestampBasedRandomNumber = (): number => {
    const now = new Date();
    const month = now.getMonth() + 1; // Months are 0-based, so add 1
    const date = now.getDate();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();

    // Create a seed using month, date, hours, minutes, and seconds
    const seed = month * 10000000 + date * 100000 + hours * 10000 + minutes * 100 + seconds;

    // Multiply by Math.random() to introduce randomness and limit it to 5 digits
    return Math.floor(seed * Math.random()) % 1000000;
  };

  const randomIdNumber = generateTimestampBasedRandomNumber();
  const [customerData, setCustomerData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    phoneNumber: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    country: "",
    city: "",
    postalCode: "",
    stateOrProvince: "",
  });
  const [orderData, setOrderData] = useState<any>({
    orderIds: [""],
    profileID: "",
    siteID: "",
    siteName: "Custom",
    userDataPresent: "",
    siteAccountID: "",
    siteOrderID: "",
    currency: "AUD",
    createdDateUtc: "",
    importDateUtc: "",
    privateNotes: "",
    specialInstructions: "",
    totalPrice: "",
    totalTaxPrice: "",
    totalShippingPrice: "",
    totalShippingTaxPrice: "",
    totalInsurancePrice: "",
    totalGiftOptionPrice: "",
    totalGiftOptionTaxPrice: "",
    additionalCostOrDiscount: "",
    requestedShippingCarrier: "",
    requestedShippingClass: "",
    flagID: "",
    distributionCenterTypeRollup: "",
    checkoutStatus: "",
    paymentStatus: "",
    appImportDateTime: "",
    shippingStatus: "",
    pickingStatus: "",
    issueStatus: "",
    issueDescription: "",
    duplicateFlag: "",
    userId: "",
    chooseShippingQuotesFlag: "",
    createShippingLabelFlag: "",
    checkoutDateUtc: "",
    paymentDateUtc: "",
    shippingDateUtc: "",
    buyerUserId: "",
    buyerEmailAddress: "",
    buyerEmailOptIn: "",
    orderTaxType: "",
    shippingTaxType: "",
    giftOptionsTaxType: "",
    paymentMethod: "",
    paymentCreditCardLast4: "",
    shippingFirstName: "",
    shippingLastName: "",
    shippingDaytimePhone: "",
    shippingAddressLine1: "",
    shippingAddressLine2: "",
    shippingCompanyName: customerData.companyName,
    shippingCity: "",
    shippingStateOrProvince: "",
    shippingStateOrProvinceName: "",
    shippingPostalCode: "",
    shippingCountry: "",
    billingTitle: "",
    billingFirstName: "",
    billingLastName: "",
    billingSuffix: "",
    billingCompanyName: "",
    billingCompanyJobTitle: "",
    billingDaytimePhone: "",
    billingEveningPhone: "",
    billingAddressLine1: "",
    billingAddressLine2: "",
    billingCity: "",
    billingStateOrProvince: "",
    billingStateOrProvinceName: "",
    billingPostalCode: "",
    billingCountry: "",
    promotionAmount: "",
    availableQuotes: {
      INHOUSE: {
        items: [
          {
            reference: "",
            quantity: "",
            weight: "",
            length: "",
            width: "",
            height: "",
          },
        ],
        carrierQuotes: [
          {
            productId: "",
            parcelType: "",
            price: 0,
            shippingClass: "",
            weight: 0,
            reference: "",
            trackingAvailable: false,
            envFlag: false,
          },
          {
            productId: "",
            parcelType: "",
            price: 0,
            shippingClass: "",
            weight: 0,
            reference: "",
            trackingAvailable: false,
            envFlag: false,
          },
        ],
      },
      DIRECTFREIGHT: {
        items: [
          {
            reference: "",
            quantity: "0",
            weight: "",
            length: "",
            width: "",
            height: "",
          },
        ],
        carrierQuotes: [
          {
            productId: "",
            parcelType: "",
            price: 0,
            shippingClass: null,
            weight: null,
            reference: null,
            trackingAvailable: null,
            envFlag: null,
          },
        ],
      },
      EPARCEL: {
        items: [
          {
            reference: "",
            quantity: "",
            weight: "",
            length: "",
            width: "",
            height: "",
          },
        ],
        carrierQuotes: [
          {
            productId: "",
            parcelType: "",
            price: 0,
            shippingClass: null,
            weight: null,
            reference: "",
            trackingAvailable: null,
            envFlag: null,
          },
        ],
      },
    },
    historyList: [
      {
        userId: "",
        action: "",
        actionTimestamp: "",
      },
    ],
    fulfillments: [
      {
        profileID: "",
        orderID: "",
        createdDateUtc: "",
        updatedDateUtc: "",
        type: "",
        deliveryStatus: "",
        trackingNumber: "",
        shippingCarrier: "",
        shippingClass: "",
        distributionCenterID: 0,
        externalFulfillmentCenterCode: " ",
        externalFulfillmentStatus: "",
        shippingCost: 0,
        insuranceCost: 0,
        taxCost: 0,
        shippedDateUtc: "",
        hasShippingLabel: false,
        hasChannelPackingSlip: false,
        hasReturnLabel: false,
        hasChannelReturnLabel: false,
        _id: randomIdNumber,
      },
    ],
    items: [
      {
        profileID: "",
        orderID: "",
        productID: "",
        siteOrderItemID: "",
        siteListingID: "",
        sku: "",
        title: "",
        quantity: "",
        unitPrice: "",
        taxPrice: "",
        shippingPrice: "",
        shippingTaxPrice: "",
        recyclingFee: "",
        unitEstimatedShippingCost: "",
        giftPrice: "",
        giftTaxPrice: "",
        itemURL: "",
        harmonizedCode: "",
        imageURL: "",
        warehouseLocation: "",
        excessStock: "",
        sortingOrder: "",
        zoneOrder: "",
        availableQty: "",
        barcode: "",
        varyingAttributes: "",
        productURL: "",
        bundleComponents: [],
        _id: randomIdNumber,
      },
    ],
    shipByDateTime: "",
    pickingDetail: {
      packingStationId: "",
    },
    siteOrderIDs: [""],
    _id: randomIdNumber,
  });

  const [cards, setCards] = useState<any[]>([]);
  const [expandedCards, setExpandedCards] = useState<boolean[]>([]);

  const toggleExpand = (cardIndex: number) => {
    setExpandedCards((prevExpandedCards) =>
      prevExpandedCards.map((expanded, index) => (index === cardIndex ? !expanded : expanded))
    );
  };

  useEffect(() => {
    if (getSKUResponse) {
      setOrderData((prevData) => {
        const existingSKUs = new Set((prevData.bundleComponents || []).map((item) => item.sku));
        const newComponents = (getSKUResponse?.response || []).filter((item) => !existingSKUs.has(item.sku));

        const updatedBundleComponents = [...(prevData.bundleComponents || []), ...newComponents];

        // Ensure items array matches bundleComponents in length and structure
        const updatedItems = updatedBundleComponents?.map((component) => {
          const matchingComponent = prevData.bundleComponents?.find((item) => item.sku === component.sku);
          // Find matching SKU from skuFieldValue array
          const matchingSKUField = skuFieldValue?.find((item) => item.sku === component.sku);

          return {
            profileID: "",
            orderID: "",
            isBundle: false,
            productID: component.id,
            siteOrderItemID: "",
            siteListingID: "",
            sku: component.sku,
            title: component.title,
            quantity: matchingSKUField?.qty || matchingComponent?.quantity || "",
            unitPrice: "",
            taxPrice: "",
            shippingPrice: "",
            shippingTaxPrice: "",
            recyclingFee: "",
            unitEstimatedShippingCost: "",
            giftPrice: "",
            giftTaxPrice: "",
            itemURL: "",
            harmonizedCode: component.harmonizedCode,
            imageURL: component.imageURL || "",
            warehouseLocation: "",
            excessStock: "",
            sortingOrder: "",
            zoneOrder: "",
            availableQty: "",
            barcode: "",
            varyingAttributes: "",
            productURL: component.productURL || "",
            bundleComponents: [],
            _id: randomIdNumber,
          };
        });
        //Returns All Mapped data of order
        return {
          orderIds: [""],
          profileID: "",
          siteID: "",
          siteName: "Custom",
          userDataPresent: "",
          siteAccountID: "",
          siteOrderID: "",
          currency: "AUD",
          createdDateUtc: "",
          importDateUtc: "",
          privateNotes: "",
          specialInstructions: "",
          totalPrice: orderData.totalPrice,
          totalTaxPrice: "",
          totalShippingPrice: orderData.totalShippingPrice,
          totalShippingTaxPrice: "",
          totalInsurancePrice: "",
          totalGiftOptionPrice: "",
          totalGiftOptionTaxPrice: "",
          additionalCostOrDiscount: "",
          requestedShippingCarrier: orderData.requestedShippingCarrier,
          requestedShippingClass: orderData.requestedShippingClass,
          flagID: "",
          distributionCenterTypeRollup: "",
          checkoutStatus: "",
          paymentStatus: "Complete",
          appImportDateTime: "",
          shippingStatus: "Unshipped",
          pickingStatus: "",
          issueStatus: "",
          issueDescription: "",
          duplicateFlag: "",
          userId: "",
          chooseShippingQuotesFlag: false,
          createShippingLabelFlag: false,
          checkoutDateUtc: "",
          paymentDateUtc: "",
          shippingDateUtc: "",
          buyerUserId: "",
          buyerEmailAddress: customerData.email,
          buyerEmailOptIn: false,
          orderTaxType: "",
          shippingTaxType: "",
          giftOptionsTaxType: "",
          paymentMethod: "",
          paymentCreditCardLast4: "",
          shippingFirstName: customerData.firstName,
          shippingLastName: customerData.lastName,
          shippingDaytimePhone: customerData.phoneNumber,
          shippingAddressLine1: customerData.addressLine1,
          shippingAddressLine2: customerData.addressLine2,
          shippingCompanyName: customerData.companyName,
          shippingCity: customerData.city,
          shippingStateOrProvince: customerData.city,
          shippingStateOrProvinceName: customerData.stateOrProvince,
          shippingPostalCode: customerData.postalCode,
          shippingCountry: customerData.country,
          billingTitle: "",
          billingFirstName: "",
          billingLastName: "",
          billingSuffix: "",
          billingCompanyName: "",
          billingCompanyJobTitle: "",
          billingDaytimePhone: "",
          billingEveningPhone: "",
          billingAddressLine1: "",
          billingAddressLine2: "",
          billingCity: "",
          billingStateOrProvince: "",
          billingStateOrProvinceName: "",
          billingPostalCode: "",
          billingCountry: "",
          promotionAmount: 0,
          availableQuotes: {
            INHOUSE: {
              items: [
                {
                  reference: "",
                  quantity: "",
                  weight: "",
                  length: "",
                  width: "",
                  height: "",
                },
              ],
              carrierQuotes: [
                {
                  productId: "",
                  parcelType: "",
                  price: 0,
                  shippingClass: "",
                  weight: 0,
                  reference: "",
                  trackingAvailable: false,
                  envFlag: false,
                },
                {
                  productId: "",
                  parcelType: "",
                  price: 0,
                  shippingClass: "",
                  weight: 0,
                  reference: "",
                  trackingAvailable: false,
                  envFlag: false,
                },
              ],
            },
            DIRECTFREIGHT: {
              items: [
                {
                  reference: "",
                  quantity: "0",
                  weight: "",
                  length: "",
                  width: "",
                  height: "",
                },
              ],
              carrierQuotes: [
                {
                  productId: "",
                  parcelType: "",
                  price: 0,
                  shippingClass: null,
                  weight: null,
                  reference: null,
                  trackingAvailable: null,
                  envFlag: null,
                },
              ],
            },
            EPARCEL: {
              items: [
                {
                  reference: "",
                  quantity: "",
                  weight: "",
                  length: "",
                  width: "",
                  height: "",
                },
              ],
              carrierQuotes: [
                {
                  productId: "",
                  parcelType: "",
                  price: 0,
                  shippingClass: null,
                  weight: null,
                  reference: "",
                  trackingAvailable: null,
                  envFlag: null,
                },
              ],
            },
          },
          historyList: [
            {
              userId: "",
              action: "",
              actionTimestamp: "",
            },
          ],
          fulfillments: [
            {
              profileID: "",
              orderID: "",
              createdDateUtc: "",
              updatedDateUtc: "",
              type: "",
              deliveryStatus: "",
              trackingNumber: "",
              shippingCarrier: "",
              shippingClass: "",
              distributionCenterID: 0,
              externalFulfillmentCenterCode: " ",
              externalFulfillmentStatus: "",
              shippingCost: 0,
              insuranceCost: 0,
              taxCost: 0,
              shippedDateUtc: "",
              hasShippingLabel: false,
              hasChannelPackingSlip: false,
              hasReturnLabel: false,
              hasChannelReturnLabel: false,
              _id: randomIdNumber,
            },
          ],
          items: updatedItems,
          shipByDateTime: "",
          pickingDetail: {
            packingStationId: "",
          },
          siteOrderIDs: [""],
          _id: randomIdNumber,
        };
      });
    }
  }, [getSKUResponse, skuFieldValue]);

  const handleInputChange = (field: string, value: string, index: number) => {
    if (!["sku", "qty"].includes(field)) {
      if (Object.keys(orderData).includes(field)) {
        setOrderData((prevData) => ({ ...prevData, [field]: value }));
      }
    }
    if (Object.keys(customerData).includes(field)) {
      setCustomerData((prevData) => ({ ...prevData, [field]: value }));
    }

    if (field === "sku" || field === "qty") {
      setSkuFieldValue((prev) =>
        prev?.map((item, i) => (i === index ? { ...item, [field]: field === "qty" ? Number(value) : value } : item))
      );
      // if (field === "sku") {
      //   setGetSKUEnabled(true);
      // }
    }

    // Update orderData.items when qty is changed
    if (field === "qty") {
      setOrderData((prevData) => {
        const updatedItems = prevData.items?.map((item) =>
          item.sku === skuFieldValue[index].sku ? { ...item, quantity: Number(value) } : item
        );

        return { ...prevData, items: updatedItems };
      });
    }
  };

  const handleAddSKUButton = (sku, index) => {
    setSkuFieldValue((prev) => [...prev, { sku: "", qty: 1 }]);
  };

  const handleRemoveSKUButton = (index: number) => {
    if (index > 0) {
      setSkuFieldValue((prev) => prev.filter((_, i) => i !== index));
    }
  };

  useEffect(() => {
    setGetSKUEnabled(false);
  }, [getSKUEnabled]);

  const [createManualOrderEnabled, setCreateManualOrderEnabled] = useState<boolean>(false);

  const {
    loadingCreateManualOrder,
    fetchingCreateManualOrder,
    errorFetchingCreateManualOrder,
    CreateManualOrderResponse,
    fetchCreateManualOrderError,
    refetchCreateManualOrder,
  } = useCreateManualOrder({ submitManualOrderData: { ...orderData }, enabled: createManualOrderEnabled });

  const handleSubmit = () => {
    setCreateManualOrderEnabled(true);
    onClose();
  };

  useEffect(() => {
    setCreateManualOrderEnabled(false);
  }, [createManualOrderEnabled]);

  useEffect(() => {
    if (CreateManualOrderResponse?.success === true) {
      window.location.reload();
    }
  });

  const handleCancel = () => {
    onClose();
    setCustomerData({
      firstName: "",
      lastName: "",
      companyName: "",
      phoneNumber: "",
      email: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      city: "",
      postalCode: "",
      stateOrProvince: "",
    });
    setOrderData({
      profileID: "",
      shippingStatus: "Unshipped",
      orderID: "",
      productID: "",
      siteOrderItemID: "",
      siteListingID: "",
      sku: [],
      title: "",
      quantity: "",
      unitPrice: "",
      taxPrice: "",
      shippingPrice: "",
      shippingTaxPrice: "",
      recyclingFee: "",
      unitEstimatedShippingCost: "",
      giftPrice: "",
      giftTaxPrice: "",
      itemURL: "",
      harmonizedCode: "",
      imageURL: "",
      warehouseLocation: "",
      excessStock: "",
      sortingOrder: "",
      zoneOrder: "",
      availableQty: "",
      barcode: "",
      varyingAttributes: "",
      productURL: "",
      requestedShippingCarrier: "",
      requestedShippingClass: "",
    });
    setSkuFieldValue([{ qty: 1, sku: "" }]);
    window.location.reload();
  };

  useEffect(() => {
    const newCards = [
      {
        title: "Customer Details:",
        customerDetails: [
          {
            id: 1,
            value: customerData.firstName,
            label: "Customer First Name",
            name: "firstName",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("firstName", e.target.value, 0),
          },
          {
            id: 2,
            value: customerData.lastName,
            label: "Customer Last Name",
            name: "lastName",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("lastName", e.target.value, 1),
          },
          {
            id: 3,
            value: customerData.companyName,
            label: "Company Name",
            name: "companyName",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("companyName", e.target.value, 2),
          },
          {
            id: 4,
            value: customerData.phoneNumber,
            label: "Phone Number",
            name: "phoneNumber",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("phoneNumber", e.target.value, 3),
          },
          {
            id: 5,
            value: customerData.email,
            label: "Email",
            name: "email",
            type: "email",
            error: null,
            onChange: (e) => handleInputChange("email", e.target.value, 4),
          },
          {
            id: 6,
            value: customerData.addressLine1,
            label: "Address Line 1",
            name: "addressLine1",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("addressLine1", e.target.value, 5),
          },
          {
            id: 7,
            value: customerData.addressLine2,
            label: "Address Line 2",
            name: "addressLine2",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("addressLine2", e.target.value, 6),
          },
          {
            id: 8,
            value: customerData.stateOrProvince,
            label: "State",
            name: "stateOrProvince",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("stateOrProvince", e.target.value, 7),
          },
          {
            id: 9,
            value: customerData.country,
            label: "Country",
            name: "country",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("country", e.target.value, 8),
          },
          {
            id: 10,
            value: customerData.city,
            label: "Suburb",
            name: "city",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("city", e.target.value, 9),
          },
          {
            id: 11,
            value: customerData.postalCode,
            label: "Postal Code",
            name: "postalCode",
            type: "text",
            error: null,
            onChange: (e) => handleInputChange("postalCode", e.target.value, 10),
          },
        ],
      },
      {
        title: "Shipping",
        orderShippingDetails: [
          {
            id: 0,
            title: "Requested Shipping Class:",
            value: orderData.requestedShippingClass,
            label: "Requested Shipping Class",
            name: "requestedShippingClass",
            options: ["Standard", "Express"],
            onChange: (e) => handleInputChange("requestedShippingClass", e.target.value, 0),
          },
          {
            id: 1,
            title: "Requested Shipping Carrier:",
            value: orderData.requestedShippingCarrier,
            label: "Requested Shipping Carrier",
            name: "requestedShippingCarrier",
            options: ["Standard", "Express"],
            onChange: (e) => handleInputChange("requestedShippingCarrier", e.target.value, 1),
          },
          {
            id: 2,
            title: "Total Price (AUD):",
            value: orderData.totalPrice,
            label: "Total Price",
            name: "totalPrice",

            onChange: (e) => handleInputChange("totalPrice", e.target.value, 2),
          },
          {
            id: 3,
            title: "Total Shipping Price (AUD):",
            value: orderData.totalShippingPrice,
            label: "Total Shipping Price",
            name: "totalShippingPrice",
            onChange: (e) => handleInputChange("totalShippingPrice", e.target.value, 3),
          },
        ],
      },
      {
        title: "Order Details",
        orderDetails: [
          { id: 0, label: "SKU", key: "sku", width: "15%", type: "text", value: skuFieldValue.flatMap((sku) => sku.sku) },
          { id: 1, label: "Quantity", key: "qty", width: "15%", type: "number", value: skuFieldValue.flatMap((sku) => sku.qty) },
        ],
      },
    ];

    setCards(newCards);
    setExpandedCards(newCards.map(() => true));
  }, [customerData, orderData]);

  const handleSKUBlur = (e, config) => {
    if (config.key === "sku" && e.target.value.trim() !== "") {
      setGetSKUEnabled(true);
    }
  };

  return (
    <Drawer
      open={open || fetchingCreateManualOrder}
      onClose={() => {
        if (!fetchingCreateManualOrder) onClose();
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Stack p={2} flex="1" gap={1}>
          {cards.map((card, cardIndex) => (
            <CardWithCollapse
              key={cardIndex}
              title={card.title}
              isExpanded={expandedCards[cardIndex]}
              onToggle={() => toggleExpand(cardIndex)}
            >
              <Box mb={3}>
                <ShipmentCustomerDetails shipments={card.customerDetails} />
              </Box>

              {card.title === "Order Details" && (
                <Stack spacing={2}>
                  {skuFieldValue?.map((sku, index) => {
                    // Find the matching SKU data from the response
                    const matchedSkuData = getSKUResponse?.response?.find((item) => item.sku === sku.sku);

                    return (
                      <Stack key={index} spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {card.orderDetails.map((config) => (
                            <TextField
                              key={config.key}
                              label={`${config.label} ${index + 1}`}
                              variant="outlined"
                              sx={{ width: config.width }}
                              value={config.key === "sku" ? sku.sku : sku.qty}
                              type={config.type}
                              required={config.key === "qty"}
                              onBlur={(e) => (config.key === "sku" ? handleSKUBlur(e, config) : undefined)}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (config.key === "qty") {
                                  if (/^\d*$/.test(value)) {
                                    handleInputChange(config.key, value, index);
                                  }
                                } else {
                                  handleInputChange(config.key, value, index);
                                }
                              }}
                            />
                          ))}

                          <Divider orientation="vertical" variant="middle" flexItem />
                          <Stack spacing={1} width="50%" direction="row">
                            {fetchingGetSKU ? (
                              <LinearProgress sx={{ width: "100%" }} />
                            ) : matchedSkuData ? (
                              <Stack>
                                <Typography>Title:</Typography>
                                <Typography>{matchedSkuData.title}</Typography>
                              </Stack>
                            ) : (
                              <Typography>No data available</Typography>
                            )}
                          </Stack>

                          <Divider orientation="vertical" variant="middle" flexItem />

                          <Button
                            onClick={() => handleAddSKUButton(sku, index)}
                            variant="contained"
                            disabled={!sku || !matchedSkuData}
                          >
                            Add SKU
                          </Button>

                          <Button disabled={index === 0} onClick={() => handleRemoveSKUButton(index)} color="error">
                            Remove
                          </Button>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              )}

              {card.title === "Shipping" && (
                <Stack direction="row" justifyContent="space-evenly">
                  {card.orderShippingDetails.map((field) => (
                    <Stack key={field.id} spacing={1} mb={2}>
                      <Typography
                        variant="subtitle2"
                        component="span"
                        sx={{
                          backgroundColor: globalTheme.background,
                          color: globalTheme.primaryColor,
                          p: 1,
                          borderRadius: 2,
                          width: "fit-content",
                        }}
                      >
                        {field.title}
                      </Typography>

                      {field.id === 2 || field.id === 3 ? (
                        <TextField
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={field.label}
                          sx={{ minWidth: 120 }}
                        />
                      ) : (
                        <Select value={field.value} onChange={field.onChange} displayEmpty sx={{ minWidth: 120 }}>
                          <MenuItem value="" disabled>
                            {field.label}
                          </MenuItem>
                          {field.options?.map((option: string) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardWithCollapse>
          ))}
        </Stack>

        <Box display="flex" justifyContent="flex-end" p={2}>
          <Button variant="outlined" onClick={handleCancel} sx={{ marginRight: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={fetchingCreateManualOrder || !getSKUResponse}>
            {fetchingCreateManualOrder ? <CircularProgress size={24} color="inherit" /> : "Submit"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
